import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  ChatContextType,
  TalkDeskState,
  generateChatKey,
  SubmissionChatData,
  StudyChatData,
  SiteChatData,
  UserChatData,
  talkdeskFeatureKey,
} from '@data-access/talk-desk/';
import {
  isSubmissionChat,
  isStudyChat,
  isSiteChat,
  isUserChat,
} from '../constants/talkdesk.constants';

export const selectTalkdeskState =
  createFeatureSelector<TalkDeskState>(talkdeskFeatureKey);

export const selectAllChats = createSelector(
  selectTalkdeskState,
  (state) => state.chats,
);

export const selectActiveChatId = createSelector(
  selectTalkdeskState,
  (state) => state.activeChatId,
);

export const selectActiveChat = createSelector(
  selectAllChats,
  selectActiveChatId,
  (chats, activeId) => (activeId ? chats[activeId] : undefined),
);

// Helper selectors for different context types
export const selectChatByContextAndId = (
  contextType: ChatContextType,
  id: string,
) =>
  createSelector(
    selectAllChats,
    (chats) => chats[generateChatKey(contextType, id)],
  );

export const selectSubmissionChat = (submissionId: string) =>
  createSelector(
    selectChatByContextAndId('submission', submissionId),
    (chat) => chat as SubmissionChatData | undefined,
  );

export const selectStudyChat = (studyId: string) =>
  createSelector(
    selectChatByContextAndId('study', studyId),
    (chat) => chat as StudyChatData | undefined,
  );

export const selectSiteChat = (siteId: string) =>
  createSelector(
    selectChatByContextAndId('site', siteId),
    (chat) => chat as SiteChatData | undefined,
  );

export const selectUserChat = (userId: string) =>
  createSelector(
    selectChatByContextAndId('user', userId),
    (chat) => chat as UserChatData | undefined,
  );

// Selectors for chats of specific types
export const selectAllSubmissionChats = createSelector(
  selectAllChats,
  (chats) =>
    Object.values(chats).filter(isSubmissionChat) as SubmissionChatData[],
);

export const selectAllStudyChats = createSelector(
  selectAllChats,
  (chats) => Object.values(chats).filter(isStudyChat) as StudyChatData[],
);

export const selectAllSiteChats = createSelector(
  selectAllChats,
  (chats) => Object.values(chats).filter(isSiteChat) as SiteChatData[],
);

export const selectAllUserChats = createSelector(
  selectAllChats,
  (chats) => Object.values(chats).filter(isUserChat) as UserChatData[],
);
